// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Search every dimension at once, with a click-to-filter distribution.

import { barChart, sparkline } from './charts';
import { histogram } from './layout';
import { card, chartBox, note, rowActivationAttrs, segmented, viewHead } from './ui';
import { esc, money, num, period, rate, tipAttr, weeks } from '../format';
import { PHYSICAL, PSYCH, divisionColor } from '../colors';
import { withDivision } from '../data';
import { openIndustryDrawer } from './industryDrawer';
import type { Bundle, ClaimRow } from '../types';

export const title = 'Explorer';

type Axis = 'industry' | 'occupation' | 'nature' | 'mechanism' | 'body';

const AXES: { value: Axis; label: string }[] = [
  { value: 'industry', label: 'Industry' },
  { value: 'occupation', label: 'Occupation' },
  { value: 'nature', label: 'Nature of injury' },
  { value: 'mechanism', label: 'How it happened' },
  { value: 'body', label: 'Body part' },
];

let axis: Axis = 'industry';
let query = '';
let bin: number | null = null;

function rowsFor(b: Bundle): ClaimRow[] {
  const src = b.claims[axis];
  return src.filter((r) => !r.total && !r.nfd && r.level === (axis === 'nature' ? 3 : 2));
}

function lastIdx(rows: ClaimRow[], key: 'count' | 'comp' | 'lost' | 'freq', periods: string[]): number {
  for (let i = periods.length - 1; i >= 0; i--) if (rows.some((r) => r[key]?.[i] !== null && r[key]?.[i] !== undefined)) return i;
  return periods.length - 1;
}

export function render(b: Bundle): string {
  const P = b.claims.periods;
  const all = rowsFor(b);
  const countIdx = lastIdx(all, 'count', P);
  const compIdx = lastIdx(all, 'comp', P);

  const q = query.trim().toLowerCase();
  const matched = all.filter((r) => !q || r.name.toLowerCase().includes(q));

  const compValues = matched.map((r) => r.comp?.[compIdx] ?? null).filter((v): v is number => v !== null);
  const bins = histogram(compValues, 10);
  const inBin = (r: ClaimRow) => {
    if (bin === null || !bins[bin]) return true;
    const v = r.comp?.[compIdx] ?? null;
    if (v === null) return false;
    const bucket = bins[bin];
    return v >= bucket.lo && (bin === bins.length - 1 ? v <= bucket.hi : v < bucket.hi);
  };

  const filtered = matched.filter(inBin).sort((a, c) => (c.count[countIdx] ?? 0) - (a.count[countIdx] ?? 0));
  const divisionOf = new Map(withDivision(b.claims.industry).map((r) => [r.code, r.division]));

  return `
    ${viewHead(
      'Explorer',
      'Show me everything, my way.',
      `Every published category on every dimension, searchable and filterable. Cost figures come from ${period(P[compIdx])} — the most recent year they are published for — and counts from ${period(P[countIdx])}.`,
    )}

    <div class="ctl-row">
      ${segmented('axis', AXES.map((a) => ({ value: a.value, label: a.label })), axis)}
      <label class="sel-lab" for="exp-search">Search</label>
      <input type="search" id="exp-search" placeholder="Search categories" value="${esc(query)}" />
      ${bin !== null && bins[bin] ? `<button type="button" class="btn btn-quiet" id="exp-clear">Median payment ${money(bins[bin].lo)}–${money(bins[bin].hi)} · Clear ✕</button>` : ''}
      <span class="ctl-note">${filtered.length} of ${all.length} categories</span>
    </div>

    ${card(
      'How much a claim costs, by category',
      `The spread of median compensation across every ${esc(AXES.find((a) => a.value === axis)!.label.toLowerCase())} category, ${period(P[compIdx])}. Click a bar to filter the table to just those categories.`,
      chartBox(
        barChart({
          values: bins.map((x) => x.count),
          labels: bins.map((x) => `$${Math.round(x.lo / 1000)}k`),
          domain: [0, Math.max(1, ...bins.map((x) => x.count)) * 1.15],
          width: 780,
          height: 220,
          color: (i) => (bin === i ? PSYCH : PHYSICAL),
          fmtY: (v) => String(Math.round(v)),
          tip: (i) => `${money(bins[i].lo)} – ${money(bins[i].hi)}\n${bins[i].count} categor${bins[i].count === 1 ? 'y' : 'ies'}\nClick to filter the table`,
          onClickAttr: (i) => `data-bin="${i}" tabindex="0" role="button" aria-label="Filter to categories paying between ${Math.round(bins[i].lo)} and ${Math.round(bins[i].hi)} dollars"`,
        }),
        'wide',
      ),
    )}

    ${card(
      'Every category',
      axis === 'industry' ? 'Click any row for its full industry record.' : 'Sorted by the number of serious claims.',
      `<div class="tbl-wrap"><table class="data" style="min-width:760px">
        <thead><tr>
          <th scope="col">Category</th>
          <th scope="col" class="num">Serious claims</th>
          <th scope="col" class="num">Median paid</th>
          <th scope="col" class="num">Time lost</th>
          ${axis === 'industry' || axis === 'occupation' ? '<th scope="col" class="num">Per M hours</th>' : ''}
          <th scope="col">Ten years</th>
        </tr></thead>
        <tbody>${filtered
          .slice(0, 200)
          .map(
            (r) => `<tr class="${axis === 'industry' ? 'clickable' : ''}"${axis === 'industry' ? ` data-code="${esc(r.code)}"${rowActivationAttrs(`${r.name}. Open the full industry record.`)}` : ''} data-tip="${tipAttr(`${r.name}\n${num(r.count[countIdx])} serious claims in ${period(P[countIdx])}\nMedian ${money(r.comp?.[compIdx] ?? null)} · ${weeks(r.lost?.[compIdx] ?? null)}`)}">
              <td class="wide">${axis === 'industry' ? `<span class="swatch-inline" style="background:${divisionColor(divisionOf.get(r.code) ?? '')}"></span>` : ''}${esc(r.name)}</td>
              <td class="num">${num(r.count[countIdx])}</td>
              <td class="num">${money(r.comp?.[compIdx] ?? null)}</td>
              <td class="num">${weeks(r.lost?.[compIdx] ?? null)}</td>
              ${axis === 'industry' || axis === 'occupation' ? `<td class="num">${rate(r.freq?.[lastIdx(all, 'freq', P)] ?? null, 2)}</td>` : ''}
              <td>${sparkline(r.count, PHYSICAL, 96, 24)}</td>
            </tr>`,
          )
          .join('')}</tbody></table></div>${filtered.length > 200 ? `<p class="ctl-note">Showing the 200 largest of ${filtered.length} matching categories — narrow the search to see the rest.</p>` : ''}`,
    )}

    ${note(
      `A dash means the value was withheld at source. Categories with a withheld median are excluded from the distribution above rather than counted as zero, so the bar heights sum to ${compValues.length}, not ${all.length}.`,
    )}
  `;
}

export function mount(root: HTMLElement, b: Bundle, rerender: () => void): void {
  root.querySelectorAll<HTMLButtonElement>('[data-seg="axis"]').forEach((btn) => {
    btn.addEventListener('click', () => { axis = btn.dataset.value as Axis; bin = null; rerender(); });
  });

  const search = root.querySelector<HTMLInputElement>('#exp-search');
  if (search) {
    let timer: number | undefined;
    search.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        query = search.value;
        rerender();
        const again = document.querySelector<HTMLInputElement>('#exp-search');
        if (again) { again.focus(); again.setSelectionRange(again.value.length, again.value.length); }
      }, 300);
    });
  }

  root.querySelector<HTMLButtonElement>('#exp-clear')?.addEventListener('click', () => { bin = null; rerender(); });

  root.querySelectorAll<SVGRectElement>('rect[data-bin]').forEach((r) => {
    const pick = () => { const i = Number(r.dataset.bin); bin = bin === i ? null : i; rerender(); };
    r.addEventListener('click', pick);
    r.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
  });

  root.querySelectorAll<HTMLTableRowElement>('tr.clickable[data-code]').forEach((tr) => {
    // Enter and Space, so the row is not mouse-only.
    tr.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tr.click(); }
    });
    tr.addEventListener('click', () => {
      const row = withDivision(b.claims.industry).find((r) => r.code === tr.dataset.code);
      if (row) openIndustryDrawer(b, row);
    });
  });
}
